import { ReactNode, RefObject, useCallback, useMemo, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { arrayMove } from '@dnd-kit/sortable'
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers'
import { DragEndEvent } from '@dnd-kit/core'
import {
  SequenceMenuDivider,
  SequenceMenu,
  SequenceMenuItemProps,
  RedRed60,
  Sortable,
  type HandleProps,
  Flex
} from '@sequencehq/core-components'
import { LineItemsTable } from 'Invoices/pages/CreateInvoicePage/Editor/components'
import { LineItem, LineItemGroup, useLineItemsTable } from './useLineItemsTable'
import { LineItemTotals } from './LineItemTotals'
import { lineItemEditorConfig } from './atoms'
import {
  deleteLineItemAtom,
  deleteLineItemGroupAtom,
  unsyncedIdsAtom,
  lineItemGroupsAtom,
  lineItemGroupsCountAtom,
  invoiceBillingPeriodDatesAtom,
  isSavePendingAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { desiredPositionRefAtom, LineItemEditor } from '../LineItemEditor'
import { customerIntegrationIdsAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { useCustomerLedgerIntegrations } from 'Invoices/hooks/useCustomerLedgerIntegrations'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useIsInvoiceEditable } from 'Invoices/hooks/useIsInvoiceEditable'

type Props = {
  tableFooter: ReactNode
}

export const SectionLineItems = ({ tableFooter }: Props) => {
  const tableGroups = useLineItemsTable()
  const [lineItemGroups, setLineItemGroups] = useAtom(lineItemGroupsAtom)
  const isEditable = useIsInvoiceEditable()

  const lineItemsGroupCount = useAtomValue(lineItemGroupsCountAtom)
  const invoiceBillingPeriodDates = useAtomValue(invoiceBillingPeriodDatesAtom)

  const [config, setLineItemEditor] = useAtom(lineItemEditorConfig)

  const { data: taxCategoriesData } = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxCategories
  )
  const taxCategories =
    taxCategoriesData?.pages?.flatMap(page => page.items) ?? []
  const defaultTaxCategoryId = taxCategories.find(t => t.isDefault)?.id

  const customerIntegrationIds = useAtomValue(customerIntegrationIdsAtom)
  const customerLedgerIntegrations = useCustomerLedgerIntegrations({
    customerIntegrationIds
  })

  const handleLineItemGroupSort = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!lineItemGroups || active.id === over?.id) return

      const oldIndex = lineItemGroups.findIndex(g => g.id === active.id)
      const newIndex = lineItemGroups.findIndex(g => g.id === over?.id)

      setLineItemGroups(arrayMove(lineItemGroups, oldIndex, newIndex))
    },
    [lineItemGroups, setLineItemGroups]
  )

  const groups = useMemo(() => {
    return tableGroups.map(lineItemGroup => (
      <LineItemGroupWithItems key={lineItemGroup.id} group={lineItemGroup} />
    ))
  }, [tableGroups])

  return (
    <Flex flexDir="column" gap="16px">
      <LineItemsTable>
        <LineItemsTable.Header showActionColumn={isEditable} />

        {!isEditable ? (
          groups
        ) : (
          <Sortable.Group
            modifiers={[
              restrictToFirstScrollableAncestor,
              restrictToVerticalAxis
            ]}
            items={tableGroups}
            onDragEnd={handleLineItemGroupSort}
          >
            {groups}
          </Sortable.Group>
        )}

        {tableFooter}
      </LineItemsTable>

      {lineItemsGroupCount > 0 && <LineItemTotals />}

      <LineItemEditor
        config={config}
        defaultTaxCategoryId={defaultTaxCategoryId}
        customerLedgerIntegrations={customerLedgerIntegrations}
        invoiceBillingPeriodDates={invoiceBillingPeriodDates}
        onDismiss={() => setLineItemEditor(null)}
        setLineItemEditor={setLineItemEditor}
      />
    </Flex>
  )
}

const LineItemGroupWithItems = ({ group }: { group: LineItemGroup }) => {
  const belowGroupRef = useRef<HTMLDivElement>(null)
  const isEditable = useIsInvoiceEditable()
  const lineItemGroupsCount = useAtomValue(lineItemGroupsCountAtom)
  const unsyncedIds = useAtomValue(unsyncedIdsAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)

  const lineItems = useMemo(() => {
    return group.lineItems.map(lineItem => (
      <RowLineItem key={lineItem.id} group={group} lineItem={lineItem} />
    ))
  }, [group])

  const isSortable: boolean =
    !isSavePending &&
    isEditable &&
    lineItemGroupsCount > 1 &&
    unsyncedIds.lineItemGroupIds.size === 0

  return (
    <>
      {!isSortable ? (
        <>
          <RowLineItemGroupHeader group={group} belowGroupRef={belowGroupRef} />
          {lineItems}
        </>
      ) : (
        <Sortable.Item key={group.id} id={group.id}>
          {handleProps => (
            <>
              <RowLineItemGroupHeader
                group={group}
                handleProps={handleProps}
                belowGroupRef={belowGroupRef}
              />

              {lineItems}
            </>
          )}
        </Sortable.Item>
      )}
      <div ref={belowGroupRef} />
    </>
  )
}

const RowLineItemGroupHeader = ({
  group,
  belowGroupRef,
  handleProps
}: {
  group: LineItemGroup
  belowGroupRef: RefObject<HTMLDivElement>
  handleProps?: HandleProps
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const deleteLineItemGroup = useSetAtom(deleteLineItemGroupAtom)
  const setLineItemEditor = useSetAtom(lineItemEditorConfig)
  const setDesiredPositionRef = useSetAtom(desiredPositionRefAtom)
  const unsyncedIds = useAtomValue(unsyncedIdsAtom)
  const isEditable = useIsInvoiceEditable()

  const triggerEdit = useCallback(() => {
    if (!ref.current) return

    setDesiredPositionRef(ref)

    setLineItemEditor({
      variant: 'EDIT_LINE_ITEM_GROUP',
      lineItemGroupId: group.id
    })
  }, [group.id, setDesiredPositionRef, setLineItemEditor])

  return (
    <div ref={ref}>
      {/* Credit grant line items are managed by the billing engine and cannot
          be edited or deleted. When hasCreditGrantLineItems is true, we disable
          click-to-edit and hide the kebab menu. The empty <></> placeholder
          keeps the grid's 32px action column allocated so rows stay aligned. */}
      <LineItemsTable.Row
        data-testid={`invoice.lineItems.lineItemGroup.${group.id}`}
        unsynced={unsyncedIds.lineItemGroupIds.has(group.id)}
        name={
          <LineItemsTable.Cell
            value={group.name}
            otherRows={
              [group.description, group.servicePeriod].filter(
                Boolean
              ) as string[]
            }
            strong
          />
        }
        onClick={
          isEditable && !group.hasCreditGrantLineItems ? triggerEdit : undefined
        }
        action={
          !isEditable ? undefined : group.hasCreditGrantLineItems ? (
            <></>
          ) : (
            <LineItemsTable.Cell.KebabMenu
              items={[
                (props: Partial<SequenceMenuItemProps>) => (
                  <SequenceMenu.Item
                    uuid={`edit-line-item-${group.id}`}
                    label="Edit line item"
                    onClick={e => {
                      e.stopPropagation()
                      triggerEdit()
                      props.setIsOpen?.(false)
                    }}
                  />
                ),
                (props: Partial<SequenceMenuItemProps>) => (
                  <SequenceMenu.Item
                    uuid={`add-sub-line-item-${group.id}`}
                    label="Add sub item"
                    onClick={e => {
                      e.stopPropagation()

                      if (!belowGroupRef.current) return

                      setDesiredPositionRef(belowGroupRef)
                      setLineItemEditor({
                        variant: 'CREATE_LINE_ITEM',
                        lineItemGroupId: group.id
                      })

                      props.setIsOpen?.(false)
                    }}
                  />
                ),
                <SequenceMenuDivider key="divider" />,
                (props: Partial<SequenceMenuItemProps>) => (
                  <SequenceMenu.Item
                    uuid={`remove-line-item-${group.id}`}
                    label="Remove line item"
                    onClick={e => {
                      e.stopPropagation()
                      deleteLineItemGroup(group.id)
                      props.setIsOpen?.(false)
                    }}
                    style={{ color: RedRed60 }}
                  />
                )
              ]}
            />
          )
        }
        dragHandle={
          handleProps ? (
            <LineItemsTable.DragHandle {...handleProps} />
          ) : undefined
        }
      />
      <div ref={ref} />
    </div>
  )
}

const RowLineItem = ({
  lineItem,
  group
}: {
  lineItem: LineItem
  group: LineItemGroup
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const deleteLineItem = useSetAtom(deleteLineItemAtom)
  const deleteLineItemGroup = useSetAtom(deleteLineItemGroupAtom)

  const setLineItemEditor = useSetAtom(lineItemEditorConfig)
  const setDesiredPositionRef = useSetAtom(desiredPositionRefAtom)
  const unsyncedIds = useAtomValue(unsyncedIdsAtom)
  const isEditable = useIsInvoiceEditable()

  const isCreditGrantLineItem = lineItem.isCreditGrantLineItem

  const triggerEdit = useCallback(() => {
    if (!isEditable || isCreditGrantLineItem) return
    if (!ref.current) return

    setDesiredPositionRef(ref)

    setLineItemEditor({
      variant: 'EDIT_LINE_ITEM',
      lineItemId: lineItem.id
    })
  }, [
    lineItem.id,
    setDesiredPositionRef,
    setLineItemEditor,
    isEditable,
    isCreditGrantLineItem
  ])

  return (
    <div ref={ref}>
      <LineItemsTable.Row
        key={lineItem.id}
        data-testid={`invoice.lineItems.lineItem.${lineItem.id}`}
        unsynced={unsyncedIds.lineItemIds.has(lineItem.id)}
        name={
          <LineItemsTable.Cell
            value={lineItem.name}
            otherRows={
              [lineItem.description, lineItem.servicePeriod].filter(
                Boolean
              ) as string[]
            }
            indentation={1}
          />
        }
        qty={<LineItemsTable.Cell value={lineItem.qty} />}
        rate={<LineItemsTable.Cell value={lineItem.rate} />}
        tax={<LineItemsTable.Cell value={lineItem.tax} />}
        amount={<LineItemsTable.Cell value={lineItem.amount} />}
        onClick={isEditable && !isCreditGrantLineItem ? triggerEdit : undefined}
        action={
          !isEditable ? undefined : isCreditGrantLineItem ? (
            <></>
          ) : (
            <LineItemsTable.Cell.KebabMenu
              items={[
                (props: Partial<SequenceMenuItemProps>) => (
                  <SequenceMenu.Item
                    uuid={`edit-line-item-${lineItem.id}`}
                    label="Edit sub item"
                    onClick={e => {
                      e.stopPropagation()
                      triggerEdit()
                      props.setIsOpen?.(false)
                    }}
                  />
                ),
                <SequenceMenuDivider key="divider" />,
                (props: Partial<SequenceMenuItemProps>) => (
                  <SequenceMenu.Item
                    uuid={`remove-line-item-${lineItem.id}`}
                    label="Remove sub item"
                    onClick={e => {
                      e.stopPropagation()

                      if (group.lineItems.length === 1) {
                        deleteLineItemGroup(group.id)
                        props.setIsOpen?.(false)
                        return
                      }

                      deleteLineItem({ groupId: group.id, id: lineItem.id })

                      props.setIsOpen?.(false)
                    }}
                    style={{ color: RedRed60 }}
                  />
                )
              ]}
            />
          )
        }
      />
    </div>
  )
}
